import { useEffect, useState } from 'react';
import { getEncodingStatus, StreamUnavailableError, type EncodingStatus } from '../services/streamUploadService';

/**
 * Watches a Bunny Stream video until it has finished encoding.
 *
 * ## Why this polls until "finished" and not until "playable"
 *
 * When the first film was migrated, the Bunny preview showed only 360p and it looked like
 * a settings problem. It was encoding in progress: Bunny publishes the lowest resolution
 * first and adds the rest as they complete, so a video becomes watchable long before it
 * is done. The backend reports that intermediate state separately, and this keeps
 * polling through it. Publishing on "playable" would put a film live at 360p while its
 * 1080p was still hours away.
 *
 * ## Why it backs off
 *
 * A two-hour 4K film can take hours to encode. Polling every few seconds for that long
 * is pointless load, so the interval stretches the longer encoding runs. It stops
 * entirely once the video is finished or has failed.
 */
export function useStreamEncodingStatus(videoId: string | null | undefined, enabled = true) {
  const [status, setStatus] = useState<EncodingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const id = videoId?.trim();
    if (!enabled || !id) {
      setStatus(null);
      setError(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const poll = async () => {
      try {
        const next = await getEncodingStatus(id);
        if (cancelled) return;
        setStatus(next);
        setError(null);
        if (next.finished || next.failed) return;
      } catch (e) {
        if (cancelled) return;
        if (e instanceof StreamUnavailableError) {
          setUnavailable(true);
          return;
        }
        // Transient failures keep polling; a single dropped request should not leave
        // the admin staring at a stale "encoding" forever.
        setError(e instanceof Error ? e.message : 'Could not read encoding status');
      }
      attempt += 1;
      // 5s for the first minute, 15s up to five minutes, then 60s.
      const delay = attempt < 12 ? 5_000 : attempt < 28 ? 15_000 : 60_000;
      timer = setTimeout(poll, delay);
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [videoId, enabled]);

  return { status, error, unavailable };
}
